import Button from 'components/ui/Button/Button';
import { SwitcherProps } from 'components/PageSwitcher';
import Payment from 'components/screens/Payment';
import { usePageContext } from 'context/hooks/usePageContext';
import { useTransferContext } from 'context/hooks/useTransferContext';
import { FC, useEffect, useState } from 'react';
import { formatCurrency } from 'utils';
import Timer from 'components/ui/TImer/Timer';
import Icon from 'components/ui/Icon/Icon';
import ErrorComponent from 'components/screens/Error';

import { ReactComponent as Spinner } from '../../assets/icons/spinner.svg';
import Timeout from 'components/screens/Timeout';
import { PAGE_SUB } from 'context/pageContext';
import useGetTransferStatus from 'hooks/useGetTransferStatus';
import { useTransactionContext } from 'context/hooks/useTransactionContext';
import { SpinnerInline } from 'components/ui/Spinner';
import { PaymentHandlerFactory } from 'services/paymentHandlers';
import { getErrorMessage } from 'services/paymentHandlers/apiError';
import { PAYMENT_PROVIDER } from 'types/enums';
// import { usePayableAmount } from 'context/hooks/usePayableAmount';
// TEMP: amount/fee breakdown hidden for now — re-enable to show it again
// import FeeBreakdown from 'components/ui/FeeBreakdown';

type CopyField = 'amount' | 'account';

const CopyButton: FC<{ copied: boolean; onCopy: () => void }> = ({
  copied,
  onCopy,
}) =>
  copied ? (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width="16"
      height="16"
      viewBox="0 0 16 16"
    >
      <path
        fill="#0284F8"
        d="M8.97 4.97a.75.75 0 0 1 1.07 1.05l-3.99 4.99a.75.75 0 0 1-1.08.02L2.324 8.384a.75.75 0 1 1 1.06-1.06l2.094 2.093L8.95 4.992zm-.92 5.14l.92.92a.75.75 0 0 0 1.079-.02l3.992-4.99a.75.75 0 1 0-1.091-1.028L9.477 9.417l-.485-.486z"
      />
    </svg>
  ) : (
    <img
      src="/icons/copy.svg"
      alt="copy"
      className="cursor-pointer"
      onClick={onCopy}
    />
  );

const PayWithTransfer: FC<SwitcherProps> = ({ amount }) => {
  const { ceviantTransactionId, provider } = useTransactionContext();
  const [copied, setCopied] = useState<CopyField | null>(null);
  const [isConfirming, setIsConfirming] = useState(false);
  const [confirmError, setConfirmError] = useState('');
  const { sub, changeSub } = usePageContext();
  const {
    virtualAccountName,
    bankCode,
    virtualAccountNumber,
    validityTime,
    underpayment,
  } = useTransferContext();
  const { error } = useGetTransferStatus(ceviantTransactionId, 5000);

  // const payable = usePayableAmount('TRANSFER'); // TEMP: only used by the hidden breakdown

  const handleCopy = async (field: CopyField, text: string) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(field);
    } catch (error) {
      alert('Unable to copy text. Clipboard access is blocked.');
    }
  };

  const handlePaid = async () => {
    const handler = PaymentHandlerFactory.transfer(
      provider as PAYMENT_PROVIDER,
    );
    setConfirmError('');

    if (handler.confirmTransfer) {
      setIsConfirming(true);
      try {
        await handler.confirmTransfer(ceviantTransactionId);
      } catch (error) {
        // A confirmation sent before the transfer lands is recoverable, so
        // keep the account details up for the customer to confirm again.
        setConfirmError(
          getErrorMessage(error, 'Your transfer has not been received yet.'),
        );
        return;
      } finally {
        setIsConfirming(false);
      }
    }

    changeSub(PAGE_SUB.PAYING);
  };

  useEffect(() => {
    const timer = setTimeout(() => setCopied(null), 2000);
    return () => clearTimeout(timer);
  }, [copied]);

  useEffect(() => {
    if (validityTime === 0) {
      changeSub(PAGE_SUB.TIMEOUT);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [validityTime]);

  if (error) {
    changeSub(PAGE_SUB.ERROR);
  }

  // After a short transfer the same account takes the balance, so the account
  // details are shown again with what is still owed.
  const isUnderpaid = sub === PAGE_SUB.UNDERPAID;
  const outstandingAmount = underpayment?.outstandingAmount;
  const amountReceived = underpayment?.amountReceived;
  const amountDue = isUnderpaid ? outstandingAmount : amount;

  // A failed initiation never produces an account, so the error screen has
  // to render without one.
  if (!virtualAccountNumber && sub !== PAGE_SUB.ERROR) {
    return <SpinnerInline />;
  }

  return (
    <>
      <div className="space-y-3 max-w-[400px] m-auto font-primary">
        {sub === PAGE_SUB.LOADING && (
          <div className="flex flex-col items-center justify-center h-full">
            <Icon
              svg={Spinner}
              size={70}
            />
            <p>Loading</p>
          </div>
        )}
        {(sub === PAGE_SUB.TRANSFER || isUnderpaid) && (
          <>
            {isUnderpaid ? (
              <>
                <h3 className="font-medium text:lg md:text-xl text-center">
                  {amountDue ? (
                    <>
                      Pay the balance of{' '}
                      <span className="text-[#0284F8]">
                        {formatCurrency(amountDue / 100, 'NGN')}
                      </span>{' '}
                      to complete your payment
                    </>
                  ) : (
                    'Pay the balance to complete your payment'
                  )}
                </h3>
                <p className="bg-[#FFF4D6] text-[#121212] text-xs text-center rounded-sm px-4 py-2">
                  {amountReceived && amountDue
                    ? `We received ${formatCurrency(amountReceived / 100, 'NGN')}, which is less than the ${formatCurrency((amountReceived + amountDue) / 100, 'NGN')} due. `
                    : 'We received less than the amount due. '}
                  Transfer the rest to the same account below.
                </p>
              </>
            ) : (
              <>
                <h3 className="font-medium text:lg md:text-xl text-center">
                  Pay{' '}
                  <span className="text-[#0284F8]">
                    {formatCurrency(amount / 100, 'NGN')}
                  </span>{' '}
                  to the account details below:
                </h3>
                <p className="bg-[#C9E5FF]/40 text-[#121212] text-xs text-center rounded-sm px-4 py-2">
                  To avoid processing delays or extra fees, please ensure you
                  pay the exact total amount. Thank you
                </p>
              </>
            )}
            <div className="space-y-6">
              <div className="space-y-6">
                {!!amountDue && (
                  <div className="flex items-center justify-between border-b-[0.5px] pb-2 border-black/30">
                    <p className="text-[#121212]/60 text-sm md:text-base font-medium">
                      {isUnderpaid ? 'Balance:' : 'Amount:'}
                    </p>
                    <p className="text-[#121212] text-sm md:text-base font-medium flex items-center gap-2">
                      {formatCurrency(amountDue / 100, 'NGN')}
                      <CopyButton
                        copied={copied === 'amount'}
                        // bare number, so it pastes straight into a bank app's amount field
                        onCopy={() =>
                          handleCopy('amount', (amountDue / 100).toFixed(2))
                        }
                      />
                    </p>
                  </div>
                )}
                {/* TEMP: amount/fee breakdown hidden for now — re-enable to show it again
                </div>
                <FeeBreakdown
                  baseAmount={payable.baseAmount}
                  fee={payable.displayFee}
                  className="hidden lg:block"
                /> */}
                <div className="flex items-center justify-between border-b-[0.5px] pb-2 border-black/30">
                  <p className="text-[#121212]/60 text-sm md:text-base font-medium">
                    Account Name:
                  </p>
                  <p className="text-[#121212] text-sm md:text-base font-medium">
                    {virtualAccountName}
                  </p>
                </div>
                {bankCode && (
                  <div className="flex items-center justify-between border-b-[0.5px] pb-2 border-black/30">
                    <p className="text-[#121212]/60 text-sm md:text-base font-medium">
                      Bank Name:
                    </p>
                    <p className="text-[#121212] text-sm md:text-base font-medium">
                      {bankCode}
                    </p>
                  </div>
                )}
                <div className="flex items-center justify-between border-b-[0.5px] pb-2 border-black/30">
                  <p className="text-[#121212]/60 text-sm md:text-base font-medium">
                    Account Number:
                  </p>
                  <p className="text-[#121212] text-sm md:text-base font-medium flex items-center gap-2">
                    {virtualAccountNumber}
                    <CopyButton
                      copied={copied === 'account'}
                      onCopy={() => handleCopy('account', virtualAccountNumber)}
                    />
                  </p>
                </div>
                {!!validityTime && <Timer />}
              </div>
              {confirmError && (
                <p className="text-red-500 text-sm text-center">
                  {confirmError}
                </p>
              )}
              <div className="w-full flex gap-4">
                <Button
                  type="submit"
                  variant="primary"
                  onClick={handlePaid}
                  disabled={isConfirming}
                >
                  {isConfirming ? (
                    <Icon
                      svg={Spinner}
                      size={28}
                      className="mx-auto text-white"
                    />
                  ) : isUnderpaid ? (
                    'I have paid the balance'
                  ) : (
                    'I have paid'
                  )}
                </Button>
                <Button
                  variant="outline"
                  type="button"
                  onClick={() =>
                    window.parent.postMessage(
                      { message: 'closeCeviantSDK' },
                      '*',
                    )
                  }
                >
                  Cancel
                </Button>
              </div>
            </div>
          </>
        )}
        {sub === PAGE_SUB.PAYING && <Payment />}
        {sub === PAGE_SUB.ERROR && <ErrorComponent />}
        {sub === PAGE_SUB.TIMEOUT && <Timeout />}
      </div>
    </>
  );
};

export default PayWithTransfer;
